import React, { useState, useEffect, useMemo } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import CircularProgress from '@mui/material/CircularProgress';


import {
  Grid,
  TextField,
  Card,
  Autocomplete,
  Icon,
} from "@mui/material";

import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";

import api from "core/interceptors/api";

export default function AdminReferral() {
  const [admins, setAdmins] = useState([]);
  const [searchId, setSearchId] = useState("");
  const [idValid, setIdValid] = useState(false);
  const [verifiedOwner, setVerifiedOwner] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const [vendorReferrals, setVendorReferrals] = useState([]);
  const [serviceProviderReferrals, setServiceProviderReferrals] = useState([]);
  const [vendorCount, setVendorCount] = useState(0);
  const [serviceProviderCount, setServiceProviderCount] = useState(0);

  const [vendorSearchQuery, setVendorSearchQuery] = useState("");
  const [filteredVendorReferrals, setFilteredVendorReferrals] = useState([]);
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorRowsPerPage, setVendorRowsPerPage] = useState(10);

  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const [filteredServiceReferrals, setFilteredServiceReferrals] = useState([]);
  const [servicePage, setServicePage] = useState(0);
  const [serviceRowsPerPage, setServiceRowsPerPage] = useState(10);

  const [totalIncentive, setTotalIncentive] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  const navigate = useNavigate();

  // Fetch all admins to populate referral ID dropdown/autocomplete
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const res = await api.get("/login/all");
        // Filter out superadmins (role === 'superadmin')
        const filteredAdmins = (Array.isArray(res.data) ? res.data : []).filter(
          admin => admin.role !== "Super Admin"
        );
        setAdmins(filteredAdmins);
      } catch (error) {
        console.error("Error fetching admins:", error);
        setAdmins([]);
      }
    };
    fetchAdmins();
  }, []);


  // Validate Referral ID
  const validateId = (() => {
  let currentRequest = 0;

  return async (id) => {
    const trimmedId = id.trim();
    if (!trimmedId) {
      setIdValid(false);
      setVerifiedOwner("");
      return;
    }

    const requestId = ++currentRequest;
    setIsVerifying(true);

    try {
      const res = await api.get(`/admin_referrals/validate_id/${trimmedId}`);
      if (requestId === currentRequest) {
        setIdValid(res.data?.valid);
        setVerifiedOwner(res.data?.owner_name || "");
      }
    } catch {
      if (requestId === currentRequest) {
        setIdValid(false);
        setVerifiedOwner("");
      }
    } finally {
      if (requestId === currentRequest) setIsVerifying(false);
    }
  };
})();


  // Handle Referral ID change in Autocomplete or typing
  const handleSearchIdChange = (event, value) => {
    const newId = typeof value === "string" ? value : value?.code || "";
    setSearchId(newId);
    validateId(newId);
  };

  // Fetch total incentive (only when searching by ID)
  const fetchTotalIncentive = async () => {
    if (!searchId.trim() || !idValid) {
      setTotalIncentive(0);
      return;
    }
    try {
      const response = await api.get("/incentive_earning/summary", {
        params: {
          referral_id: searchId,
          from_date: fromDate,
          to_date: toDate,
        },
      });
      setTotalIncentive(response.data.total_incentive_earned || 0);
    } catch (error) {
      console.error("Error fetching total incentive:", error);
      setTotalIncentive(0);
    }
  };

  // Fetch Referrals given current filters: only referral ID search mode now
  const fetchAdminReferrals = async () => {
    setLoading(true);
    try {
      const role = localStorage.getItem("role");
      const queryParams = new URLSearchParams();

      if (searchId && idValid) {
        queryParams.append("id", searchId);
      } else {
        setLoading(false);
        return;
      }
      if (fromDate) queryParams.append("from_date", fromDate);
      if (toDate) queryParams.append("to_date", toDate);
      if (role) queryParams.append("role", role);

      const res = await api.get(`/admin_referrals?${queryParams.toString()}`);

      const vendors = Array.isArray(res.data.vendors) ? res.data.vendors.filter(Boolean) : [];
      const services = Array.isArray(res.data.service_providers) ? res.data.service_providers.filter(Boolean) : [];

      setVendorReferrals(vendors);
      setServiceProviderReferrals(services);
      setVendorCount(vendors.length);
      setServiceProviderCount(services.length);

      setFilteredVendorReferrals(vendors);
      setVendorPage(0);
      setVendorSearchQuery("");

      setFilteredServiceReferrals(services);
      setServicePage(0);
      setServiceSearchQuery("");

      await fetchTotalIncentive();
    } catch (error) {
      console.error("Error fetching admin referrals:", error);
      setVendorReferrals([]);
      setServiceProviderReferrals([]);
      setVendorCount(0);
      setServiceProviderCount(0);
      setFilteredVendorReferrals([]);
      setFilteredServiceReferrals([]);
      setTotalIncentive(0);
    } finally {
      setLoading(false);
    }
  };

  // Vendor referrals search filter effect
  useEffect(() => {
    const lowerCaseQuery = vendorSearchQuery.toLowerCase();
    const filtered = vendorReferrals.filter((v) =>
      v.business_name?.toLowerCase().includes(lowerCaseQuery) ||
      v.owner_name?.toLowerCase().includes(lowerCaseQuery) ||
      v.email?.toLowerCase().includes(lowerCaseQuery) ||
      v.business_type?.toLowerCase().includes(lowerCaseQuery) ||
      v.mobile_number?.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredVendorReferrals(filtered);
    setVendorPage(0);
  }, [vendorSearchQuery, vendorReferrals]);

  // Service provider referrals search filter effect
  useEffect(() => {
    const lowerCaseQuery = serviceSearchQuery.toLowerCase();
    const filtered = serviceProviderReferrals.filter((s) =>
      s.owner_name?.toLowerCase().includes(lowerCaseQuery) ||
      s.email?.toLowerCase().includes(lowerCaseQuery) ||
      s.mobile_number?.toLowerCase().includes(lowerCaseQuery) ||
      s.services_type?.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredServiceReferrals(filtered);
    setServicePage(0);
  }, [serviceSearchQuery, serviceProviderReferrals]);

  // Pagination handlers
  const handleVendorPageChange = (event, newPage) => setVendorPage(newPage);
  const handleVendorRowsPerPageChange = (event) => {
    setVendorRowsPerPage(parseInt(event.target.value, 10));
    setVendorPage(0);
  };
  const handleServicePageChange = (event, newPage) => setServicePage(newPage);
  const handleServiceRowsPerPageChange = (event) => {
    setServiceRowsPerPage(parseInt(event.target.value, 10));
    setServicePage(0);
  };

  // Memoize paginated data slices
  const paginatedVendorData = useMemo(() => {
    return filteredVendorReferrals.slice(
      vendorPage * vendorRowsPerPage,
      vendorPage * vendorRowsPerPage + vendorRowsPerPage
    );
  }, [filteredVendorReferrals, vendorPage, vendorRowsPerPage]);

  const paginatedServiceData = useMemo(() => {
    return filteredServiceReferrals.slice(
      servicePage * serviceRowsPerPage,
      servicePage * serviceRowsPerPage + serviceRowsPerPage
    );
  }, [filteredServiceReferrals, servicePage, serviceRowsPerPage]);

  // DataTable column definitions
  const vendorColumns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "serial",
      align: "center",
      Cell: ({ row }) => vendorPage * vendorRowsPerPage + row.index + 1,
      disableSortBy: true,
    },
    { Header: "Business Name", accessor: "business_name", sortable: true, align: "center" },
    { Header: "Owner Name", accessor: "owner_name", sortable: true, align: "center" },
    { Header: "Email", accessor: "email", sortable: true, align: "center" },
    { Header: "Business Type", accessor: "business_type", sortable: true, align: "center" },
    { Header: "Mobile Number", accessor: "mobile_number", sortable: true, align: "center" },
  ], [vendorPage, vendorRowsPerPage]);

  const serviceProviderColumns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "serial",
      align: "center",
      Cell: ({ row }) => servicePage * serviceRowsPerPage + row.index + 1,
      disableSortBy: true,
    },
    { Header: "Owner Name", accessor: "owner_name", sortable: true, align: "center" },
    { Header: "Email", accessor: "email", sortable: true, align: "center" },
    { Header: "Mobile Number", accessor: "mobile_number", sortable: true, align: "center" },
    { Header: "Service Type", accessor: "services_type", sortable: true, align: "center" },
  ], [servicePage, serviceRowsPerPage]);

  // Prepare rows for table (serial calculated in Cell)
  const vendorReferralsData = paginatedVendorData.map(v => ({
    business_name: v.business_name,
    owner_name: v.owner_name,
    email: v.email,
    business_type: v.business_type,
    mobile_number: v.mobile_number,
  }));

  const serviceProviderReferralsData = paginatedServiceData.map(s => ({
    owner_name: s.owner_name,
    email: s.email,
    mobile_number: s.mobile_number,
    services_type: s.services_type,
  }));

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center" px={3} pt={2}>
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>

      <DashboardNavbar />

      <MDBox pt={4} pb={3} px={3}  >
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5" fontWeight="medium" mb={2}>
              Referral Overview
            </MDTypography>

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={admins}
                  getOptionLabel={(option) => `${option.code} (${option.email})`}
                  freeSolo
                  value={admins.find(a => a.code === searchId) || null}
                  onChange={handleSearchIdChange}
                  onInputChange={(e, newInputValue) => {
                    setSearchId(newInputValue);
                    validateId(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Referral ID"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Icon sx={{ mr: 1, color: "text.secondary" }}>
                              <PersonSearchIcon />
                            </Icon>
                            {isVerifying && (
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                            )}
                          </>
                        ),
                      }}
                      error={searchId.trim() !== "" && !idValid && !isVerifying}
                      helperText={
                        searchId.trim() !== "" ? (
                          isVerifying ? (
                            "Validating..."
                          ) : idValid ? (
                            <MDTypography
                              variant="body2"
                              color="success.main"
                              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                              <CheckCircleIcon fontSize="small" /> Verified Owner: {verifiedOwner}
                            </MDTypography>
                          ) : (
                            "Invalid Referral ID"
                          )
                        ) : ""
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ mr: 1, color: "text.secondary" }}>
                        <CalendarTodayIcon />
                      </Icon>
                    ),
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ mr: 1, color: "text.secondary" }}>
                        <CalendarTodayIcon />
                      </Icon>
                    ),
                  }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <MDButton
                  onClick={fetchAdminReferrals}
                  color="info"
                  disabled={
                    loading ||
                    !searchId.trim() ||
                    !idValid ||
                    !fromDate ||
                    !toDate
                  }
                  fullWidth
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Search Referrals"}
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

        <MDBox mt={3} display="flex" justifyContent="flex-start" alignItems="center">
          <MDTypography
            variant="h6"
            fontWeight="bold"
            color="primary"
            sx={{
              backgroundColor: '#fff9c4',  // subtle highlight background (light yellow)
              padding: '6px 12px',
              borderRadius: 1,
              boxShadow: 1,
              userSelect: "none"
            }}
          >
            Total Incentive Earned: ₹{totalIncentive}
          </MDTypography>
        </MDBox>


        <MDBox px={3} mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <SummaryCard
                title="Total Vendor Referrals"
                count={vendorCount}
                color="info"
                icon={<StorefrontIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SummaryCard
                title="Total Service Provider Referrals"
                count={serviceProviderCount}
                color="success"
                icon={<MiscellaneousServicesIcon fontSize="large" />}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox pt={7} pb={3} px={3}>
          {loading ? (
            <Loader />
          ) : (filteredVendorReferrals.length === 0 && filteredServiceReferrals.length === 0) ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <MDTypography variant="h6" color="text.secondary">
                No referrals found for this admin.
              </MDTypography>
            </MDBox>
          ) : (
            <Grid container spacing={6}>
              {/* Vendor Referrals */}
              <Grid item xs={12}s>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <StorefrontIcon sx={{ color: "white" }} />
                    <MDTypography variant="h6" color="white">
                      Vendor Referrals
                    </MDTypography>
                  </MDBox>

                  <MDBox pt={3} sx={{ overflowX: "auto" }}>
                    {filteredVendorReferrals.length === 0 ? (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="200px"
                      >
                        <MDTypography color="error" variant="h6">
                          No Vendor Data Available
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                      <MDBox p={3}>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Search Vendor Referrals..."
                          value={vendorSearchQuery}
                          onChange={(e) => setVendorSearchQuery(e.target.value)}
                          sx={{ mb: 2, width: "300px" }}
                        />
                        <DataTable
                          table={{ columns: vendorColumns, rows: vendorReferralsData }}
                          isSorted={true}
                          entriesPerPage={{
                            defaultValue: vendorRowsPerPage,
                            entries: [5, 10, 15, 20, 25],
                          }}
                          showTotalEntries={true}
                          canSearch={false}
                          noEndBorder
                          pagination={{
                            variant: "contained",
                            color: "dark",
                            page: vendorPage,
                            onPageChange: handleVendorPageChange,
                            rowsPerPage: vendorRowsPerPage,
                            onRowsPerPageChange: handleVendorRowsPerPageChange,
                            count: filteredVendorReferrals.length,
                          }}
                        />
                        </MDBox>
                      </>
                    )}
                  </MDBox>
                </Card>
              </Grid>

              {/* Service Provider Referrals */}
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="success"
                    borderRadius="lg"
                    coloredShadow="success"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <MiscellaneousServicesIcon sx={{ color: "white" }} />
                    <MDTypography variant="h6" color="white">
                      Service Provider Referrals
                    </MDTypography>
                  </MDBox>

                  <MDBox pt={3} sx={{ overflowX: "auto" }}>
                    {filteredServiceReferrals.length === 0 ? (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="200px"
                      >
                        <MDTypography color="error" variant="h6">
                          No Service Provider Data Available
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                      <MDBox  p={3}>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Search Service Provider Referrals..."
                          value={serviceSearchQuery}
                          onChange={(e) => setServiceSearchQuery(e.target.value)}
                          sx={{ mb: 2, width: "300px" }}
                        />
                        <DataTable
                          table={{ columns: serviceProviderColumns, rows: serviceProviderReferralsData }}
                          isSorted={true}
                          entriesPerPage={{
                            defaultValue: serviceRowsPerPage,
                            entries: [5, 10, 15, 20, 25],
                          }}
                          showTotalEntries={true}
                          canSearch={false}
                          noEndBorder
                          pagination={{
                            variant: "contained",
                            color: "dark",
                            page: servicePage,
                            onPageChange: handleServicePageChange,
                            rowsPerPage: serviceRowsPerPage,
                            onRowsPerPageChange: handleServiceRowsPerPageChange,
                            count: filteredServiceReferrals.length,
                          }}
                        />
                        </MDBox>
                      </>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          )}
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

const Loader = () => (
  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
    <CircularProgress color="info" />
    <MDTypography variant="h6" color="text" ml={2}>
      Loading Referrals...
    </MDTypography>
  </MDBox>
);

const SummaryCard = ({ title, count, color, icon }) => (
  <Card
    sx={{
      p: 3,
      textAlign: "center",
      boxShadow: 3,
      transition: "transform 0.2s",
      "&:hover": { transform: "translateY(-5px)" },
    }}
  >
    <MDBox mb={1} display="flex" justifyContent="center" alignItems="center" color={color}>
      {icon}
    </MDBox>
    <MDTypography variant="h6" color="text.secondary" gutterBottom>
      {title}
    </MDTypography>
    <MDTypography variant="h4" color={color} fontWeight="bold">
      {count}
    </MDTypography>
  </Card>
);
